import { Component, OnDestroy, signal, WritableSignal, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { NavigationEnd, Route, Router, RouterLink } from '@angular/router'
import { filter } from 'rxjs/operators'
import { Subscription } from 'rxjs'
import { BuilderService } from '../../../builder-services/builder.service'
import { LoginService } from '../../../login/login.service'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule, MatButtonModule],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.scss'
})
export class BreadcrumbsComponent implements OnDestroy {
  public readonly parts: WritableSignal<Array<{ path: string; label: string }>> = signal([])
  public readonly section: WritableSignal<string> = signal('General')
  public readonly sectionPath: WritableSignal<string | null> = signal(null)

  private sub: Subscription
  public builderService = inject(BuilderService)
  public loginService = inject(LoginService)
  private router = inject(Router)

  constructor() {
    this.sub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateBreadcrumbs())

    this.updateBreadcrumbs()
  }

  private updateBreadcrumbs(): void {
    const url = this.router.url.split('?')[0]

    if (url === '/' || url === '') {
      this.section.set('Builder')
      this.parts.set([])
      return
    }

    const segments = url.split('/').filter((s) => s)
    const breadcrumbs: Array<{ path: string; label: string }> = []
    let activeSection = 'General'
    let activeSectionPath: string | null = null

    // Walk each prefix of the URL and try to match a route
    for (let i = 1; i <= segments.length; i++) {
      const partialPath = '/' + segments.slice(0, i).join('/')
      const matched = this.findMatchingRoute(partialPath)

      if (matched?.data?.['section']) {
        activeSection = matched.data['section']
      }
      if (matched?.data?.['sectionPath']) {
        activeSectionPath = matched.data['sectionPath']
      }
      if (matched?.data?.['breadcrumb']) {
        breadcrumbs.push({ path: partialPath, label: matched.data['breadcrumb'] })
      }
    }

    this.section.set(activeSection)
    this.sectionPath.set(activeSectionPath)
    this.parts.set(breadcrumbs)
  }

  /** Find a route whose path pattern matches the given absolute path. */
  private findMatchingRoute(absolutePath: string): Route | null {
    const segments = absolutePath.split('/').filter((s) => s)
    for (const route of this.router.config) {
      if (route.path !== undefined && this.routeMatches(route.path, segments)) {
        return route
      }
    }
    return null
  }

  /** Returns true when routePath (e.g. "orders/single-order/:orderId") matches the given segments. */
  private routeMatches(routePath: string, segments: string[]): boolean {
    const routeSegs = routePath.split('/').filter((s) => s)
    if (routeSegs.length !== segments.length) return false
    return routeSegs.every((rs, i) => rs.startsWith(':') || rs === segments[i])
  }

  openSidebar(event: Event): void {
    this.builderService.sidebarOpened = true
    event.stopPropagation()
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe()
    }
  }
}
